import { ArrowLeft, ChevronRight, Clock, DollarSign, FileText, Loader2, Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { sowiftService } from "@/lib/services";
import type { SowiftProjectStatus, SowiftProjectSummary } from "@/types";

const STATUS_LABELS: Record<SowiftProjectStatus, string> = {
  draft: "Draft",
  gathering_context: "Gathering Context",
  analyzing: "Analyzing",
  generating: "Generating",
  review: "Review",
  complete: "Complete",
};

const STATUS_COLORS: Record<SowiftProjectStatus, string> = {
  draft: "bg-surface-tertiary/20 text-content-tertiary",
  gathering_context: "bg-blue-500/20 text-blue-400",
  analyzing: "bg-yellow-500/20 text-yellow-400",
  generating: "bg-purple-500/20 text-purple-400",
  review: "bg-orange-500/20 text-orange-400",
  complete: "bg-green-500/20 text-green-400",
};

export function SowiftProjectList() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<SowiftProjectSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await sowiftService.listProjects();
      setProjects(response.data);
    } catch (error) {
      console.error("Failed to load projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter((p) => {
    const q = searchQuery.toLowerCase();
    return (
      p.name.toLowerCase().includes(q) ||
      p.clientName.toLowerCase().includes(q) ||
      (p.description?.toLowerCase().includes(q) ?? false)
    );
  });

  const formatCurrency = (value: number | null | undefined, currency: string) => {
    if (value == null) return null;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="shrink-0 p-6 border-b border-border-primary">
        <div className="flex items-center gap-3 mb-1">
          <button
            onClick={() => navigate("/apps")}
            className="p-1 text-content-tertiary hover:text-content-primary hover:bg-interactive-active rounded-sm transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-content-primary">SoWift</h1>
            <p className="text-content-tertiary mt-1">Generate Statements of Work swiftly</p>
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-quaternary" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-surface-primary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
            />
          </div>
          <button
            onClick={() => navigate("/apps/sowift/new")}
            className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <Plus className="h-4 w-4" />
            New Project
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {(() => {
          if (loading) {
            return (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
              </div>
            );
          }
          if (filteredProjects.length > 0) {
            return (
              <div className="space-y-3">
                {filteredProjects.map((project) => (
                  <div
                    key={project.uuid}
                    role="button" // NOSONAR — contains nested interactive elements
                    tabIndex={0}
                    onClick={() => navigate(`/apps/sowift/${project.uuid}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/apps/sowift/${project.uuid}`);
                      }
                    }}
                    className="bg-surface-primary border border-border-primary rounded-xl p-5 hover:border-border-secondary cursor-pointer transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-medium text-content-primary truncate">{project.name}</h3>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[project.status]}`}
                          >
                            {STATUS_LABELS[project.status]}
                          </span>
                        </div>
                        <p className="text-content-tertiary text-sm mt-1">
                          {project.clientName}
                          {project.description && (
                            <span className="text-content-quaternary"> &mdash; {project.description}</span>
                          )}
                        </p>
                        <div className="flex items-center gap-4 mt-3 text-xs text-content-quaternary">
                          <span className="flex items-center gap-1">
                            <FileText className="h-3.5 w-3.5" />
                            {project.contextItemCount} context {project.contextItemCount === 1 ? "item" : "items"}
                          </span>
                          <span className="flex items-center gap-1">
                            <FileText className="h-3.5 w-3.5" />
                            {project.documentCount} {project.documentCount === 1 ? "document" : "documents"}
                          </span>
                          {(project.romLow != null || project.romHigh != null) && (
                            <span className="flex items-center gap-1">
                              <DollarSign className="h-3.5 w-3.5" />
                              {project.romLow != null && project.romHigh != null
                                ? `${formatCurrency(project.romLow, project.currency)} - ${formatCurrency(project.romHigh, project.currency)}`
                                : formatCurrency(project.romLow ?? project.romHigh, project.currency)}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {new Date(project.updatedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-content-quaternary group-hover:text-content-tertiary transition-colors shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            );
          }
          return (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="h-16 w-16 rounded-2xl bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20 flex items-center justify-center mb-4">
                <FileText className="h-8 w-8 text-brand-primary" />
              </div>
              <h3 className="text-lg font-medium text-content-primary">
                {searchQuery ? "No projects found" : "No projects yet"}
              </h3>
              <p className="text-content-tertiary mt-1 text-center max-w-sm">
                {searchQuery
                  ? "Try adjusting your search"
                  : "Create your first project to start generating Statements of Work."}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => navigate("/apps/sowift/new")}
                  className="mt-4 flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  <Plus className="h-4 w-4" />
                  New Project
                </button>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
